import { observer } from "mobx-react-lite";
import React from "react";

import { LOCK_NODE_EDITOR_CLASSNAME } from "src/authoringMultiplayerLock/constants";
import { useVersionResourceLock } from "src/authoringMultiplayerLock/useVersionResourceLock";
import { TitleEditor } from "src/base-components/TitleEditor";
import { ResourceType } from "src/clients/flow-api";
import { DEFAULT_UNTITLED_NODE_NAME } from "src/constants/DefaultValues";
import { Node } from "src/constants/NodeDataTypes";
import { NODE_TYPE } from "src/constants/NodeTypes";
import { useCanAuthoringEditFlowVersion } from "src/hooks/useCanAuthoringEditFlowVersion";
import { useGraphStore } from "src/store/StoreProvider";

const getValue = (selectedNode: Node | undefined) => {
  if (selectedNode?.data.label === DEFAULT_UNTITLED_NODE_NAME) {
    return undefined;
  }
  return selectedNode?.data.label;
};

export const getInputOutputNodeTitle = (selectedNode: Node) => {
  if (selectedNode?.type === NODE_TYPE.INPUT_NODE) {
    return "Overall Input";
  } else if (selectedNode?.type === NODE_TYPE.OUTPUT_NODE) {
    return "Overall Output";
  }

  return undefined;
};

export const NodeTitleEditor: React.FC = observer(() => {
  const { selectedNode, updateNode } = useGraphStore();

  const { lockedByOtherUser } = useVersionResourceLock(
    ResourceType.NODE,
    selectedNode?.id,
  );

  const canEditFlowVersion = useCanAuthoringEditFlowVersion();

  const isNotEditableNodeTitle =
    selectedNode?.type === NODE_TYPE.INPUT_NODE ||
    selectedNode?.type === NODE_TYPE.OUTPUT_NODE;

  const isDisabled = !canEditFlowVersion || lockedByOtherUser;

  if (isNotEditableNodeTitle) {
    return (
      <span className="w-full text-gray-800 font-inter-semibold-16px">
        {getInputOutputNodeTitle(selectedNode)}
      </span>
    );
  }

  return (
    // Keep -translate, it compensates 1px border of input inside editor
    <div className="-ml-1.5 -translate-x-px">
      <TitleEditor
        className={LOCK_NODE_EDITOR_CLASSNAME}
        dataLoc="node-title-editor"
        disabled={isDisabled}
        placeholder={DEFAULT_UNTITLED_NODE_NAME}
        value={getValue(selectedNode)}
        onSubmit={(val) => updateNode({ newName: val })}
      />
    </div>
  );
});
